import { useEffect } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import { Box, Flex, Text } from '@chakra-ui/react'

import { DeviceFeed } from '@/features/device-feed'
import {
  MSEVideoPlayer,
  VideoFeedEvent,
  VideoPlayer,
  useFindDeviceFeed,
} from '@/features/video-player'
import { useDevicePopoverDetailsQuery } from '@/graphql/generated/hooks'
import { DeviceType } from '@/graphql/generated/schemas'
import { AnalyticsScreen, useMixpanel } from '@/utils/analytics'

import { getDeviceIcon } from '../../utils/DeviceIcons'

interface DevicePopoverContentIProps {
  deviceId: string
  deviceName: string
  deviceType: DeviceType
}

export const DevicePopoverContent = ({
  deviceId,
  deviceName,
  deviceType,
}: DevicePopoverContentIProps) => {
  const isCameraDevice = deviceType === DeviceType.Camera
  const icon = getDeviceIcon(deviceType)

  // Feature flag for MSE-based authenticated proxy streaming
  const isMSEStreamingEnabled = useFeatureFlag(
    FeatureFlag.authenticatedProxyStreaming
  )

  const { data, loading: isLoading } = useDevicePopoverDetailsQuery({
    variables: {
      id: deviceId,
    },
    nextFetchPolicy: 'cache-first',
  })

  const { createCameraEventHandler, markStreamRequested } = useMixpanel(
    AnalyticsScreen.DeviceHoverModal
  )

  useEffect(() => {
    markStreamRequested()
  }, [])

  const mixpanelData = () => {
    return {
      device_id: deviceId,
      facility_id: data?.device?.facility?.id,
      facility_address: data?.device?.facility?.address,
      floor_id: data?.device?.floor?.id,
      floor_name: data?.device?.floor?.name,
      streaming_type: data?.device?.camera?.streamingType,
    }
  }

  const handleCameraEvent = createCameraEventHandler(mixpanelData())

  const {
    stream: { url: cameraFeedUrl, isLoading: isFeedDataLoading, errorMessage },
    nextCamera,
    previousCamera,
    availableCamerasIds,
    currentCameraId,
  } = useFindDeviceFeed(deviceId, handleCameraEvent)

  return (
    <Flex data-testid='DevicePopoverContent:wrapper' direction='column'>
      {isCameraDevice && (
        <Box p='8px' shadow='xl'>
          <DeviceFeed
            isLoading={isFeedDataLoading || isLoading}
            onClickNext={nextCamera}
            onClickPrevious={previousCamera}
            showCameraSwitcher={availableCamerasIds.length > 1}
          >
            {isMSEStreamingEnabled && currentCameraId ? (
              <MSEVideoPlayer
                autoplay={true}
                cameraId={currentCameraId}
                errorLabel={errorMessage}
                onEvent={handleCameraEvent}
                onStartPlaying={() => handleCameraEvent(VideoFeedEvent.Playing)}
                placeholderHeight='204px'
                showControls={true}
              />
            ) : (
              <VideoPlayer
                autoplay={true}
                errorLabel={errorMessage}
                liveEnabled={true}
                liveOnly
                onStartPlaying={() => handleCameraEvent(VideoFeedEvent.Playing)}
                pipActive={false}
                placeholderHeight='204px'
                url={cameraFeedUrl}
              />
            )}
          </DeviceFeed>
        </Box>
      )}
      <Flex alignItems='center' direction='row' p='16px' shadow='md'>
        {icon}
        <Text
          color='black'
          fontSize='16px'
          fontWeight='medium'
          marginLeft='12px'
        >
          {deviceName}
        </Text>
      </Flex>
    </Flex>
  )
}
